(function attachCombinedProductSection(globalScope) {
  const modelApi = globalScope && globalScope.CombinedProductModel;

  if (!modelApi) {
    return;
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('DOMContentLoaded', () => {
      document.querySelectorAll('.combined-product-section').forEach((container) => {
        initCombinedProductSection(container);
      });
    });
  }

  function initCombinedProductSection(container) {
    const refs = getCombinedProductRefs(container);
    if (!refs.dataScript || !refs.familyOptions || !refs.durationOptions || !refs.packageList) {
      return null;
    }

    let rawSectionData;
    try {
      rawSectionData = JSON.parse(refs.dataScript.textContent);
    } catch (error) {
      console.error('Failed to parse combined product section data.', error);
      return null;
    }

    const model = modelApi.normalizeCombinedProductData(rawSectionData);
    const requestedTab = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('tab') : null;
    const state = modelApi.createCombinedProductState(model, requestedTab);

    if (!state) return null;

    render(state, refs);

    container.addEventListener('click', (event) => {
      const familyButton = event.target.closest('[data-family-key]');
      if (familyButton && container.contains(familyButton)) {
        event.preventDefault();
        modelApi.setActiveFamily(state, familyButton.dataset.familyKey);
        render(state, refs);
        return;
      }

      const durationButton = event.target.closest('[data-duration-key]');
      if (durationButton && container.contains(durationButton)) {
        event.preventDefault();
        if (durationButton.disabled) return;
        modelApi.setActiveDuration(state, durationButton.dataset.durationKey);
        render(state, refs);
        return;
      }

      const packageCard = event.target.closest('[data-package-card]');
      if (packageCard && container.contains(packageCard)) {
        event.preventDefault();
        if (packageCard.disabled) return;
        modelApi.setSelectedPackage(state, packageCard.dataset.variantId);
        render(state, refs);
        return;
      }

      const submitTrigger = event.target.closest('[data-submit-active-variant]');
      if (submitTrigger && container.contains(submitTrigger)) {
        event.preventDefault();
        submitActiveVariant(state);
      }
    });
    return { refs, state };
  }

  function getCombinedProductRefs(container) {
    return {
      dataScript: container.querySelector('[data-role="combined-product-data"]'),
      familyOptions: container.querySelector('[data-role="family-options"]'),
      durationOptions: container.querySelector('[data-role="duration-options"]'),
      packageList: container.querySelector('[data-role="package-list"]'),
      packageContext: container.querySelector('[data-role="package-context"]'),
      summaryRows: container.querySelector('[data-role="summary-rows"]'),
      summaryHelper: container.querySelector('[data-role="summary-helper"]'),
      summaryTotal: container.querySelector('[data-role="summary-total"]'),
      summaryNote: container.querySelector('[data-role="summary-note"]'),
      infoButtons: Array.from(container.querySelectorAll('[data-modal-target="#product-info-modal"]')),
      mobileBar: container.querySelector('[data-role="mobile-bar"]'),
      mobileFamily: container.querySelector('[data-role="mobile-family"]'),
      mobileSelection: container.querySelector('[data-role="mobile-selection"]'),
      mobileNote: container.querySelector('[data-role="mobile-note"]'),
      mobilePrice: container.querySelector('[data-role="mobile-price"]'),
      submitTriggers: Array.from(container.querySelectorAll('[data-submit-active-variant]')),
    };
  }

  function render(state, refs) {
    const selectedPackage = modelApi.ensureActiveSelection(state);
    const activeFamily = modelApi.getActiveFamily(state);

    if (!activeFamily || !selectedPackage) return;

    renderFamilyOptions(state, refs.familyOptions, activeFamily);
    renderDurationOptions(refs.durationOptions, activeFamily);
    renderPackageList(state, refs, activeFamily, selectedPackage);
    renderSummary(state, refs, activeFamily, selectedPackage);
    syncModalContent(state);
  }

  function renderFamilyOptions(state, familyOptions, activeFamily) {
    familyOptions.setAttribute('role', 'radiogroup');
    familyOptions.setAttribute('aria-label', 'Choose plan type');
    familyOptions.innerHTML = state.families
      .map((family) => {
        const isSelected = family.key === activeFamily.key;

        return `
          <button
            type="button"
            class="cp-family-card ${isSelected ? 'is-selected' : ''}"
            data-family-key="${escapeHtml(family.key)}"
            role="radio"
            aria-checked="${isSelected ? 'true' : 'false'}"
          >
            <span class="cp-family-card__header">
              <span class="cp-choice-indicator" aria-hidden="true"></span>
              <span class="cp-family-card__content">
                <span class="cp-family-card__label">${escapeHtml(family.label)}</span>
                <span class="cp-family-card__copy">${escapeHtml(family.description)}</span>
              </span>
            </span>
          </button>
        `;
      })
      .join('');

    familyOptions.classList.toggle('cp-family-grid--single', state.families.length === 1);
  }

  function renderDurationOptions(durationOptions, activeFamily) {
    durationOptions.setAttribute('role', 'radiogroup');
    durationOptions.setAttribute('aria-label', 'Select duration');
    durationOptions.innerHTML = activeFamily.durations
      .map((duration) => {
        const isSelected = duration.key === activeFamily.selectedDurationKey;

        return `
          <button
            type="button"
            class="cp-duration-chip ${isSelected ? 'is-selected' : ''}"
            data-duration-key="${escapeHtml(duration.key)}"
            ${duration.hasAvailable ? '' : 'disabled'}
            role="radio"
            aria-checked="${isSelected ? 'true' : 'false'}"
          >
            <span class="cp-choice-indicator" aria-hidden="true"></span>
            <span class="cp-duration-chip__label">${escapeHtml(duration.label)}</span>
          </button>
        `;
      })
      .join('');
  }

  function renderPackageList(state, refs, activeFamily, selectedPackage) {
    const durationPackages = modelApi.getPackagesForDuration(activeFamily, activeFamily.selectedDurationKey);
    refs.packageList.setAttribute('role', 'radiogroup');
    refs.packageList.setAttribute('aria-label', 'Choose data package');
    refs.packageList.classList.toggle('cp-package-list--single', durationPackages.length === 1);
    refs.packageList.classList.toggle('cp-package-list--pair', durationPackages.length === 2);

    if (refs.packageContext) {
      refs.packageContext.textContent = durationPackages.length
        ? `Showing data options for ${selectedPackage.durationLabel}.`
        : `No packages are available for this duration right now.`;
    }

    if (!durationPackages.length) {
      refs.packageList.innerHTML = '<div class="cp-empty-state">No packages are currently available for this duration.</div>';
      return;
    }

    refs.packageList.innerHTML = durationPackages
      .map((pkg) => {
        const isSelected = String(pkg.variantId) === String(selectedPackage.variantId);
        const actionLabel = state.labels.soldOut || 'Sold out';

        return `
          <button
            type="button"
            class="cp-package-card ${isSelected ? 'is-selected' : ''} ${pkg.available ? '' : 'is-unavailable'}"
            data-package-card
            data-variant-id="${escapeHtml(pkg.variantId)}"
            role="radio"
            aria-checked="${isSelected ? 'true' : 'false'}"
            ${pkg.available ? '' : 'disabled'}
          >
            <div class="cp-package-card__header">
              <div class="cp-package-card__body">
                <span class="cp-choice-indicator" aria-hidden="true"></span>
                <div class="cp-package-card__copy">
                  ${pkg.packageEyebrow && pkg.packageEyebrow.toLowerCase() !== 'unlimited' ? `<p class="cp-package-card__eyebrow">${escapeHtml(pkg.packageEyebrow)}</p>` : ''}
                  <h3 class="cp-package-card__title">
                    <span class="cp-package-card__title-main">${escapeHtml(pkg.cardTitle || pkg.packageTitle)}</span>
                  </h3>
                  ${pkg.cardSupportingLine ? `<p class="cp-package-card__supporting">${escapeHtml(pkg.cardSupportingLine)}</p>` : ''}
                  <strong class="cp-package-card__price">${escapeHtml(pkg.priceLabel)}</strong>
                </div>
              </div>
              ${!pkg.available ? `<div class="cp-package-card__actions"><span class="cp-package-card__status">${escapeHtml(actionLabel)}</span></div>` : ''}
            </div>
          </button>
        `;
      })
      .join('');
  }

  function renderSummary(state, refs, activeFamily, selectedPackage) {
    const summaryRows = [
      { label: 'Plan Type', value: activeFamily.label },
      { label: 'Duration', value: selectedPackage.durationLabel },
      { label: 'Data Details', value: selectedPackage.summaryLabel },
    ];

    if (refs.summaryRows) {
      refs.summaryRows.innerHTML = summaryRows
        .map(
          (row) => `
            <div class="cp-summary-row">
              <span>${escapeHtml(row.label)}</span>
              <strong>${escapeHtml(row.value)}</strong>
            </div>
          `
        )
        .join('');
    }

    if (refs.summaryHelper) {
      refs.summaryHelper.textContent = selectedPackage.summaryHelperText || '';
    }

    if (refs.summaryTotal) {
      refs.summaryTotal.textContent = selectedPackage.priceLabel;
    }

    if (refs.summaryNote) {
      const summaryNoteHtml = String((state.labels && state.labels.summaryNoteHtml) || '').trim();
      refs.summaryNote.innerHTML = summaryNoteHtml;
      refs.summaryNote.hidden = !summaryNoteHtml;
    }

    refs.submitTriggers.forEach((button) => {
      button.textContent = selectedPackage.available ? state.labels.addToCart || 'Add to cart' : state.labels.soldOut || 'Sold out';
      button.disabled = !selectedPackage.available;
    });

    refs.infoButtons.forEach((button) => {
      button.dataset.activeProduct = activeFamily.modalKey;
    });

    if (refs.mobileFamily) {
      refs.mobileFamily.textContent = `${activeFamily.label} • ${selectedPackage.durationLabel}`;
    }

    if (refs.mobileSelection) {
      refs.mobileSelection.textContent = selectedPackage.summaryLabel;
    }

    if (refs.mobileNote) {
      refs.mobileNote.textContent = selectedPackage.summaryHelperText || '';
    }

    if (refs.mobilePrice) {
      refs.mobilePrice.textContent = selectedPackage.priceLabel;
    }

    if (refs.mobileBar) {
      refs.mobileBar.classList.toggle('is-hidden', !selectedPackage.available && !selectedPackage.priceLabel);
    }
  }

  function syncModalContent(state) {
    state.families.forEach((family) => {
      const modalContent = document.querySelector(`.cp-modal-content[data-product="${family.modalKey}"]`);
      if (modalContent) {
        modalContent.innerHTML = modelApi.renderDetailsMarkup(family.details, {
          family,
          selectedPackage: modelApi.getSelectedPackage(family),
        });
      }
    });
  }

  function submitActiveVariant(state) {
    const activeFamily = modelApi.getActiveFamily(state);
    const selectedPackage = modelApi.getSelectedPackage(activeFamily);

    if (!activeFamily || !selectedPackage || !selectedPackage.available) return;

    const form = document.getElementById(activeFamily.formId);
    if (!form) return;

    const input = form.querySelector('input[name="id"]');
    if (input) {
      input.disabled = false;
      input.value = selectedPackage.variantId;
    }

    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.click();
      return;
    }

    form.submit();
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      getCombinedProductRefs,
      initCombinedProductSection,
    };
  }
})(typeof globalThis !== 'undefined' ? globalThis : this);
